import type { ReactNode } from 'react'
import { Box, Tooltip } from '@mui/material'

import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { RADIUS } from '@/shared/styles/radii'
import { DURATION } from '@/shared/styles/motion'
import { FONT_WEIGHT, scaledFontSize } from '@/shared/styles/typography'

interface RowProps {
  children: ReactNode
  /** 直前の要素との間隔。録音後の画面はカード (Paper) の外に置くので 1 段あける。 */
  gapTop?: number
}

// ページ下部の二次アクションの行 (器)。左寄せ・折り返し・上に hairline。
// 録音前 (PatientActionMenu) と録音後 (RecordActionRow) で同じ文法にする
// (design-book 02-components/patient-info-bar.md)。
export const ActionRow = ({ children, gapTop = 0 }: RowProps) => (
  <Box
    sx={{
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: 0.5,
      mt: gapTop,
      pt: 1,
      borderTop: `1px solid ${colorTokens.border.subtle}`,
    }}
  >
    {children}
  </Box>
)

interface Props {
  label: string
  onClick: () => void
  /** design-book 02-components/popover-menu.md の Beta タグ (bg.muted / micro). */
  beta?: boolean
  disabled?: boolean
  /** disabled の理由や補足。hover 時だけ出す (行の文字は増やさない). */
  tooltip?: string
}

// 低 emphasis なテキスト action (= 上の行の 1 つ).
// 幾何は同系の MasterInsertAction に揃える: 文字は scaledFontSize で文字サイズモードに
// 追従させ (ADR-0019)、器は minHeight (固定 height だと「大」で文字がはみ出す)。
export const ActionLink = ({
  label,
  onClick,
  beta,
  disabled,
  tooltip,
}: Props): ReactNode => {
  const button = (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      disabled={disabled}
      sx={{
        display: 'inline-flex',
        alignItems: 'baseline',
        gap: 0.5,
        minHeight: 28,
        px: 1,
        border: 'none',
        borderRadius: `${RADIUS.md}px`,
        bgcolor: 'transparent',
        cursor: disabled ? 'default' : 'pointer',
        fontFamily: 'inherit',
        fontSize: scaledFontSize('label'),
        fontWeight: FONT_WEIGHT.medium,
        // 文字の途中で折り返さない (狭い幅では行ごと次の行へ)
        whiteSpace: 'nowrap',
        color: disabled ? colorTokens.fg.faint : colorTokens.fg.muted,
        transition: `background-color ${DURATION.fast}ms, color ${DURATION.fast}ms`,
        '&:hover': disabled
          ? {}
          : {
              bgcolor: colorTokens.overlay.hover,
              color: colorTokens.fg.default,
            },
        '&:focus': { outline: 'none' },
        '&:focus-visible': { boxShadow: colorTokens.focus.ringNeutral },
      }}
    >
      {label}
      {beta && (
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            minHeight: 16,
            px: 0.75,
            ml: 0.25,
            borderRadius: `${RADIUS.sm}px`,
            bgcolor: colorTokens.bg.muted,
            color: colorTokens.fg.muted,
            fontSize: scaledFontSize('micro'),
            fontWeight: FONT_WEIGHT.medium,
          }}
        >
          Beta
        </Box>
      )}
    </Box>
  )
  // disabled な button は hover event も focus も出さないので、tooltip は span で包み、
  // keyboard からも理由に届くよう tabIndex を与える (MUI Tooltip の作法)。
  return tooltip ? (
    <Tooltip title={tooltip} placement="top">
      <Box
        component="span"
        tabIndex={0}
        sx={{ display: 'inline-flex', borderRadius: `${RADIUS.md}px` }}
      >
        {button}
      </Box>
    </Tooltip>
  ) : (
    button
  )
}
